/**
 * Display labels for the User Permission editor.
 * Keys match backend module_list columns; labels align with the sidebar (use-navigation).
 */
export const getPermissionStructure = ({ isNabhEnabled = false, isOvertimeEnabled = false } = {}) => [
    { key: 'home', label: 'Home', type: 'single' },
    {
        key: 'administrator',
        label: 'Administrator',
        type: 'group',
        subItems: [
            { key: 'organization', label: 'Organization' },
            { key: 'user_group', label: 'User Group' },
            { key: 'user_permission', label: 'User Permission' },
            { key: 'notice_board', label: 'Notice Board' },
            { key: 'password_policy', label: 'Password Policy' },
            { key: 'organization_documents', label: 'Organization Documents' },
        ],
    },
    {
        key: 'master',
        label: 'Master',
        type: 'group',
        subItems: [
            { key: 'employee_type', label: 'Employee Type' },
            { key: 'department', label: 'Department' },
            { key: 'designation', label: 'Designation' },
            { key: 'city', label: 'City' },
            { key: 'block', label: 'Block' },
            { key: 'floor', label: 'Floor' },
            { key: 'room', label: 'Room' },
            { key: 'leave_type', label: 'Leave Type' },
            { key: 'employee_group', label: 'Employee Group' },
            { key: 'reimbursements_type', label: 'Reimbursements Type' },
            { key: 'asset_type', label: 'Asset Type' },
            { key: 'asset_category', label: 'Asset Category' },
            { key: 'asset_sub_category', label: 'Asset Sub Category' },
            { key: 'letter_type', label: 'Letter Type' },
            { key: 'letter_template', label: 'Letter Template' },
            { key: 'document_type', label: 'Document Type' },
        ],
    },
    {
        key: 'configuration',
        label: 'Configuration',
        type: 'group',
        subItems: [
            { key: 'shift_configuration', label: 'Shift Configuration' },
            { key: 'holiday', label: 'Holiday' },
            { key: 'week_off', label: 'Week off' },
            { key: 'payroll_period', label: 'Payroll Period' },
            { key: 'subordinate', label: 'Subordinate' },
            { key: 'custom_configuration', label: 'Custom Configuration' },
            { key: 'salary_component', label: 'Salary Component' },
            { key: 'salary_template', label: 'Salary Template' },
            { key: 'da_revision', label: 'DA Revision' },
        ],
    },
    {
        key: 'employee',
        label: 'Employee',
        type: 'group',
        subItems: [
            { key: 'employees', label: 'Employees' },
            { key: 'employee_remarks', label: 'Employee Remarks' },
            { key: 'employee_allowances', label: 'Employee Allowances' },
            { key: 'employee_deductions', label: 'Employee Deductions' },
            { key: 'employee_deposits', label: 'Employee Deposits' },
            { key: 'leave_request', label: 'Leave Request' },
            { key: 'reimbursements_request', label: 'Reimbursement Request' },
            { key: 'advance_salary_request', label: 'Advance Salary Request' },
            ...(isOvertimeEnabled ? [{ key: 'overtime_request', label: 'Overtime Request' }] : []),
            { key: 'resignation', label: 'Resignation' },
            { key: 'subordinates', label: 'Subordinates' },
            { key: 'branch_transfer', label: 'Branch Transfer' },
            { key: 'transfer_reporting_manager', label: 'Transfer Reporting Manager' },
            { key: 'absconding', label: 'Abscond' },
            { key: 'approval_inbox', label: 'Approval Inbox' },
            { key: 'timesheet', label: 'My Tasks' },
            { key: 'team_timesheet', label: 'Team Tasks' },
            { key: 'employee_onboarding', label: 'Employee Onboarding' },
            { key: 'intern_onboarding', label: 'Intern Onboarding' },
        ],
    },
    {
        key: 'payroll',
        label: 'Payroll',
        type: 'group',
        subItems: [
            { key: 'run_payroll', label: 'Run Payroll' },
            { key: 'payroll', label: 'Payroll' },
            { key: 'payslips', label: 'Payslips' },
            { key: 'tds_preview', label: 'TDS Preview' },
            { key: 'tds_configuration', label: 'TDS Configuration' },
            { key: 'payroll_hold', label: 'Payroll Hold' },
            { key: 'attendance_penalties', label: 'Attendance Penalties' },
            { key: 'perk_criteria', label: 'Perk Criteria' },
        ],
    },
    {
        key: 'attendance',
        label: 'Attendance',
        type: 'group',
        subItems: [
            { key: 'attendance_dashboard', label: 'Attendance Dashboard' },
            { key: 'day_wise_attendance', label: 'Day Attendance' },
            { key: 'attendance_log', label: 'Attendance Log' },
            { key: 'punch_discrepancies', label: 'Punch Discrepancies' },
            { key: 'penalty_appeals', label: 'Penalty Appeals' },
            { key: 'shift_change_requests', label: 'My Shift Change / Swap' },
        ],
    },
    {
        key: 'asset_management',
        label: 'Asset Management',
        type: 'group',
        subItems: [
            { key: 'asset_request', label: 'Asset Request' },
            { key: 'asset_registry', label: 'Asset Registry' },
            { key: 'asset_employee_audit', label: 'Asset Employee Audit' },
        ],
    },
    {
        key: 'reports',
        label: 'Reports',
        type: 'group',
        subItems: [
            { key: 'branch_wise_activity_report', label: 'Branch Wise Activity Report' },
            { key: 'day_wise_attendance', label: 'Day wise Attendance' },
            { key: 'month_wise_attendance', label: 'Month Wise Attendance' },
            { key: 'employee_esi_statement', label: 'Employee ESI Statement' },
            { key: 'employee_pf_statement', label: 'Employee PF Statement' },
            { key: 'salary_process_bank', label: 'Salary Process Bank' },
            { key: 'payroll_detailed_summary', label: 'Payroll Detailed Summary' },
            { key: 'employee_earning_deduction', label: 'Employee Earning Deduction' },
            { key: 'muster_roll', label: 'Muster Roll' },
            { key: 'subordinates', label: 'Subordinates' },
            { key: 'employee_salary_summary', label: 'Employee Salary Summary' },
            { key: 'attendance_correction', label: 'Attendance Correction' },
            { key: 'roster_report', label: 'Roster Report' },
            { key: 'leave_permission_report', label: 'Leave Permission Report' },
            { key: 'short_duty_report', label: 'Short-Duty Review' },
            { key: 'fine_report', label: 'Fine Report' },
            { key: 'document_upload_report', label: 'Document Upload Report' },
        ],
    },
    ...(isNabhEnabled ? [{
        key: 'training',
        label: 'Training',
        type: 'group',
        subItems: [
            { key: 'my_trainings', label: 'My Trainings' },
            { key: 'team_trainings', label: 'Team Trainings' },
            { key: 'assign_training', label: 'Assign Training' },
        ],
    }] : []),
    {
        key: 'appraisal',
        label: 'Appraisal',
        type: 'group',
        subItems: [
            { key: 'my_appraisals', label: 'My Appraisal' },
            { key: 'team_appraisals', label: 'Team Review' },
            { key: 'appraisal_kpis', label: 'KPI Manager' },
            { key: 'appraisal_cycles', label: 'Cycles' },
            { key: 'appraisal_approval', label: 'Final Approval' },
        ],
    },
    {
        key: 'biometric',
        label: 'Biometric',
        type: 'group',
        subItems: [
            { key: 'biometric_devices', label: 'Biometric Devices' },
            { key: 'biometric_employee_mapping', label: 'Biometric Employee Mapping' },
            { key: 'biometric_logs', label: 'Biometric Logs' },
        ],
    },
    {
        key: 'grievance',
        label: 'Grievance',
        type: 'group',
        subItems: [
            { key: 'my_grievances', label: 'My Grievances' },
            { key: 'grievance_inbox', label: 'Grievance Inbox' },
            { key: 'grievance_categories', label: 'Grievance Categories' },
            { key: 'grievance_dashboard', label: 'Grievance Dashboard' },
        ],
    },
    {
        key: 'roster',
        label: 'Roster',
        type: 'group',
        subItems: [
            { key: 'roster', label: 'Roster' },
            { key: 'duty_roster', label: 'Duty Roster' },
            { key: 'shift_change_request', label: 'Shift Change Approvals' },
        ],
    },
    {
        key: 'internship',
        label: 'Internship',
        type: 'group',
        subItems: [
            { key: 'internship_programs', label: 'Programs' },
            { key: 'internship_enrolments', label: 'Enrolments' },
            { key: 'my_internship', label: 'My Internship' },
        ],
    },
];
